import React from 'react';
import { Table, Badge } from '@/components/ui';
import type { MaintenancePlan } from '../types/maintenance-plan.types';

export interface PlanDesktopTableProps {
  data: MaintenancePlan[];
  isLoading: boolean;
  onRowClick: (plan: MaintenancePlan) => void;
}

export function PlanDesktopTable({ data, isLoading, onRowClick }: PlanDesktopTableProps) {
  const getTaskTypeLabel = (taskType: string) => {
    const map: Record<string, string> = {
      mowing: 'Cắt cỏ',
      pruning: 'Cắt tỉa',
      watering: 'Tưới nước',
      fertilizing: 'Bón phân',
      spraying: 'Phun thuốc',
      cleaning: 'Dọn vệ sinh',
    };
    return map[taskType] || taskType;
  };

  const getStatusBadge = (status: MaintenancePlan['status']) => {
    switch (status) {
      case 'draft': return <Badge variant="neutral">Nháp</Badge>;
      case 'pending_approval': return <Badge variant="warning">Chờ duyệt</Badge>;
      case 'approved': return <Badge variant="info">Đã duyệt</Badge>;
      case 'active': return <Badge variant="success">Đang hiệu lực</Badge>;
      case 'paused': return <Badge variant="warning">Tạm dừng</Badge>;
      case 'expired': return <Badge variant="danger">Hết hiệu lực</Badge>;
      case 'cancelled': return <Badge variant="neutral">Đã hủy</Badge>;
      default: return null;
    }
  };

  return (
    <Table
      columns={[
        { key: 'code', title: 'Mã KH' },
        { key: 'name', title: 'Tên kế hoạch' },
        { key: 'zone', title: 'Khu vực' },
        { key: 'tasks', title: 'Công việc' },
        { key: 'frequency', title: 'Tần suất' },
        { key: 'period', title: 'Thời gian hiệu lực' },
        { key: 'resources', title: 'Nguồn lực dự kiến' },
        { key: 'progress', title: 'Đã tạo lịch' },
        { key: 'status', title: 'Trạng thái', align: 'center' },
      ]}
      data={data}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderCell={(item, key) => {
        switch (key) {
          case 'code':
            return <span className="font-mono text-sm font-medium">{item.code}</span>;
          case 'name':
            return <span className="font-semibold text-os-text">{item.name}</span>;
          case 'zone':
            return <span className="text-sm">{item.zone_name}</span>;
          case 'tasks':
            return (
              <div className="flex flex-wrap gap-1">
                {item.task_types.slice(0, 2).map(t => (
                  <span key={t} className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-os-muted">
                    {getTaskTypeLabel(t)}
                  </span>
                ))}
                {item.task_types.length > 2 && (
                  <span className="text-xs bg-slate-100 px-1.5 py-0.5 rounded text-os-muted">
                    +{item.task_types.length - 2}
                  </span>
                )}
              </div>
            );
          case 'frequency':
            return <span className="text-sm">{item.frequency}</span>;
          case 'period':
            return (
              <span className="text-xs text-os-muted">
                {new Date(item.start_date).toLocaleDateString('vi-VN')} - {new Date(item.end_date).toLocaleDateString('vi-VN')}
              </span>
            );
          case 'resources':
            return (
              <div className="flex flex-col text-xs">
                <span className="font-medium">{item.standard_personnel} người</span>
                <span className="text-os-muted">{item.estimated_hours} giờ</span>
              </div>
            );
          case 'progress':
            return (
              <div className="flex items-center gap-2">
                <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-brand-500" 
                    style={{ width: `${(item.schedules_created / (item.schedules_total || 1)) * 100}%` }} 
                  />
                </div>
                <span className="text-xs text-os-muted">{item.schedules_created}/{item.schedules_total}</span>
              </div>
            );
          case 'status':
            return getStatusBadge(item.status);
          default:
            return String(item[key as keyof MaintenancePlan] || '');
        }
      }}
    />
  );
}
